import BotFormComponent from '../components/bot-form.component';
import { BotUpdate } from '../types/bot.types';
import { useBotGet, useBotUpdate } from '../hooks/use-bot.hook';
import { message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  extractCreateError,
  extractValidationError,
} from '../../../../../services/extract-api-error.service';
import { useEffect, useState, useCallback } from 'react';
import HeaderRow from '../../../../common/HeaderRow';
import { ValidationError } from '../../../../../types/api-response.types';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { DefaultValues } from 'react-hook-form';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import PageTitle from '@components/common/PageTitle';

interface FormSubmitHandler {
  (data: BotUpdate, resetForm: () => void): void;
}

const BotUpdateContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  if (!id)
    return (
      <ErrorFallback
        details={t('common.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );

  const { data, isLoading, error } = useBotGet(id);
  const [formDefaultValues, setFormDefaultValues] = useState<DefaultValues<BotUpdate>>({});

  const extractFormDefaultValues = useCallback(async () => {
    if (!data?.data) return;
    const { data: botData } = data;

    setFormDefaultValues({
      name: botData.name,
      jobId: (botData as any).job?.id || (botData as any).jobId,
      isVideoRecEnable: botData.isVideoRecEnable || false,
    });
  }, [data]);

  useEffect(() => {
    let isMounted = true; // to prevent memory leaks
    extractFormDefaultValues();
    return () => {
      isMounted = false;
    };
  }, [extractFormDefaultValues]);

  const { mutateAsync: updateBot, isPending } = useBotUpdate();

  const onFormSubmit: FormSubmitHandler = useCallback(
    async (data, resetForm) => {
      // Reset validation errors on new submission
      setValidationErrors([]);
      try {
        await updateBot({ id, data });
        message.success(t('crud.update.success', { entity: t('bots.title') }));
        navigate(-1);
      } catch (error: any) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        const errorMessage = extractCreateError(
          error,
          t('crud.update.error', { entity: t('bots.title') })
        );
        message.error(errorMessage);
      }
    },
    [id, updateBot, t, navigate]
  );

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.update.title', { entity: t('bots.title') })} />
        <HeaderRow
          title={t('crud.update.title', { entity: t('bots.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.update.title', { entity: t('bots.title') })} />
      <HeaderRow
        title={t('crud.update.title', { entity: t('bots.title') })}
        showBackButton={true}
      />
      <div className="row mt-3">
        <BotFormComponent
          onFormSubmit={onFormSubmit}
          isLoading={isPending || isLoading}
          defaultValues={formDefaultValues}
          columns={3}
          validationErrors={validationErrors}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(BotUpdateContainer, 'bot.update');
